/**
 * wayai update-credential — update / rotate an existing organization credential.
 *
 * Targets a credential by its display name (org credentials are resolved by name
 * everywhere), then maps to the existing `PUT /api/setup/organization-credentials/:id`.
 * The backend propagates a rotated secret to every linked connection.
 *
 * Usage:
 *   echo "$NEW_TOKEN" | wayai update-credential --name "clinica-medica-mcp" --stdin
 *   wayai update-credential --name "openai-key" --tag clinica-medica
 *   wayai update-credential --name "old-name" --rename "new-name"
 *   wayai update-credential --name "my-api" --description "Rotated 2026-06" --environment production
 */

import { requireAuth } from '../lib/auth.js';
import { ApiClient, ApiError } from '../lib/api-client.js';
import { promptSecret, prompt, readStdin, resolveOrganizationId } from '../lib/utils.js';
import {
  type AuthType,
  VALID_ENVIRONMENTS,
  type Environment,
  normalizeCliEnvironment,
  mapSecretToCredentialFields,
  resolveTagIds,
  splitTagRefs,
} from '../lib/credential-utils.js';
import type { UpdateOrgCredentialInput } from '@wayai/core/contracts';

interface ParsedArgs {
  name: string | undefined;
  rename: string | undefined;
  orgId: string | undefined;
  description: string | undefined;
  tags: string[];
  hasTagFlag: boolean;
  environment: string | undefined;
  /** --stdin: read the new secret from a pipe. */
  stdin: boolean;
  /** --secret: prompt for the new secret interactively (masked). */
  secretPrompt: boolean;
}

export function parseArgs(args: string[]): ParsedArgs {
  let name: string | undefined;
  let rename: string | undefined;
  let orgId: string | undefined;
  let description: string | undefined;
  const tags: string[] = [];
  let hasTagFlag = false;
  let environment: string | undefined;
  let stdin = false;
  let secretPrompt = false;

  for (let i = 0; i < args.length; i++) {
    if (args[i] === '--name' && args[i + 1]) {
      name = args[++i];
    } else if (args[i] === '--rename' && args[i + 1]) {
      rename = args[++i];
    } else if (args[i] === '--org' && args[i + 1]) {
      orgId = args[++i];
    } else if (args[i] === '--description' && args[i + 1]) {
      description = args[++i];
    } else if (args[i] === '--tag' && args[i + 1]) {
      hasTagFlag = true;
      tags.push(args[++i]);
    } else if (args[i] === '--environment' && args[i + 1]) {
      environment = args[++i];
    } else if (args[i] === '--stdin') {
      stdin = true;
    } else if (args[i] === '--secret') {
      secretPrompt = true;
    }
  }

  return { name, rename, orgId, description, tags: splitTagRefs(tags), hasTagFlag, environment, stdin, secretPrompt };
}

export async function updateCredentialCommand(args: string[]): Promise<void> {
  const parsed = parseArgs(args);

  if (!parsed.name) {
    console.error('Missing required flag: --name <credential-name>');
    console.error('Usage: wayai update-credential --name "my-key" --stdin');
    process.exit(1);
  }

  if (parsed.environment && !normalizeCliEnvironment(parsed.environment)) {
    console.error(`Invalid environment: "${parsed.environment}"`);
    console.error(`Supported environments: ${VALID_ENVIRONMENTS.join(', ')}`);
    process.exit(1);
  }

  const wantsSecret = parsed.stdin || parsed.secretPrompt;

  if (!wantsSecret && !parsed.rename && parsed.description === undefined && !parsed.hasTagFlag && !parsed.environment) {
    console.error('Nothing to update. Provide at least one of: --stdin/--secret, --rename, --description, --tag, --environment');
    process.exit(1);
  }

  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  const { organizationId } = await resolveOrganizationId(client, parsed.orgId);

  // Resolve the credential by display name.
  const list = await client.listOrgCredentials(organizationId);
  const matches = (list.data || []).filter((c) => c.credential_display_name === parsed.name);

  if (matches.length === 0) {
    console.error(`No credential named "${parsed.name}" found in this organization.`);
    const available = (list.data || []).map((c) => c.credential_display_name);
    if (available.length > 0) {
      console.error(`Available credentials: ${available.join(', ')}`);
    }
    process.exit(1);
  }
  if (matches.length > 1) {
    console.error(
      `${matches.length} credentials are named "${parsed.name}" (ids: ${matches.map((c) => c.organization_credential_id).join(', ')}).`,
    );
    console.error('Names are ambiguous — resolve the duplicates in Settings → Organization → Credentials before updating by name.');
    process.exit(1);
  }

  const credential = matches[0];
  const authType = credential.authentication_type as AuthType;

  const body: UpdateOrgCredentialInput = {};

  // Secret rotation — map the piped/prompted value to the right field(s) for this
  // credential's auth type.
  if (wantsSecret) {
    let input: string;
    if (parsed.stdin) {
      if (process.stdin.isTTY) {
        console.error('--stdin requires piped input. Use --secret for an interactive masked prompt.');
        process.exit(1);
      }
      input = await readStdin();
    } else if (authType === 'basic_auth') {
      const username = await prompt('Enter username: ');
      const password = await promptSecret('Enter password: ');
      input = `${username}:${password}`;
    } else {
      input = await promptSecret(authType === 'bearer' ? 'Enter access token: ' : 'Enter API key: ');
    }
    if (!input) {
      console.error('No secret provided.');
      process.exit(1);
    }
    try {
      Object.assign(body, mapSecretToCredentialFields(authType, input));
    } catch (err) {
      console.error(err instanceof Error ? err.message : String(err));
      process.exit(1);
    }
  }

  if (parsed.rename) body.credential_display_name = parsed.rename;
  if (parsed.description !== undefined) body.credential_description = parsed.description;
  if (parsed.environment) body.environment = parsed.environment as Environment;
  if (parsed.hasTagFlag) {
    const orgTags = await client.listOrgTags(organizationId);
    body.tags = resolveTagIds(parsed.tags, orgTags.data || []);
  }

  console.log(`Updating credential "${parsed.name}"...`);

  let result;
  try {
    result = await client.updateOrgCredential(credential.organization_credential_id, organizationId, body);
  } catch (err) {
    if (err instanceof ApiError && err.status === 409) {
      console.error(`\nCannot rename: a credential named "${parsed.rename}" already exists in this organization.`);
      process.exit(1);
    }
    throw err;
  }

  console.log(`\nCredential updated: ${result.data.credential_display_name}`);
  console.log(`  ID: ${result.data.organization_credential_id}`);
  console.log(`  Type: ${result.data.authentication_type}`);
  if (wantsSecret) {
    console.log('  Secret rotated — linked connections updated.');
  }
}
